/**
 * Facturation : solde, bons de commande, factures.
 *
 * Le cycle est : BON DE COMMANDE -> virement -> FACTURE ACQUITTEE.
 * Aucune facture a payer n'est jamais emise - il n'y a donc jamais
 * d'avoir a produire quand un client renonce.
 *
 * Deux consequences que cet ecran doit assumer franchement :
 *
 *   - une recharge n'est PAS instantanee. Le dire avant, pas apres :
 *     un client qui croit son credit disponible et decouvre le contraire
 *     au moment d'envoyer une enveloppe urgente est un client perdu.
 *   - la REFERENCE doit figurer dans le libelle du virement. C'est le
 *     seul lien entre l'argent recu et le compte a crediter ; sans elle,
 *     le rapprochement devient une enquete et la recharge traine.
 */
import { useCallback, useEffect, useState } from "react";

import { ApiError, api, type DemandeRecharge, type Facture, type Solde } from "../api";
import { FicheClient } from "./FicheClient";
import { Chargement, Erreur, Vide, date, montant, telecharger, useRelectureAuRetour } from "../components/ui";

const MONTANTS_SUGGERES = [10000, 25000, 50000, 100000];

/** Seuls ces roles peuvent ENGAGER de l'argent (cf. permissions.py cote
 *  serveur). Proposer le bouton aux autres reviendrait a offrir une
 *  action qui echoue systematiquement - le serveur refuse de toute
 *  facon, c'est lui qui fait autorite ; ceci evite juste la deception. */
const ROLES_QUI_PEUVENT_RECHARGER = ["owner", "admin", "billing"];

export function Billing({ role }: { role: string | null | undefined }) {
  const peutRecharger = ROLES_QUI_PEUVENT_RECHARGER.includes(role ?? "");
  const [solde, setSolde] = useState<Solde | null>(null);
  const [factures, setFactures] = useState<Facture[]>([]);
  const [recharges, setRecharges] = useState<DemandeRecharge[]>([]);
  const [erreur, setErreur] = useState<unknown>(null);
  const [chargement, setChargement] = useState(true);

  const [montantChoisi, setMontantChoisi] = useState(MONTANTS_SUGGERES[1] ?? 25000);
  const [envoi, setEnvoi] = useState(false);
  const [nouvelle, setNouvelle] = useState<DemandeRecharge | null>(null);

  const charger = useCallback(async () => {
    try {
      const [s, f, r] = await Promise.all([
        api.solde(),
        api.factures().catch(() => ({ total: 0, invoices: [] })),
        api.recharges().catch(() => ({ top_ups: [] })),
      ]);
      setSolde(s);
      setFactures(f.invoices ?? []);
      setRecharges(r.top_ups ?? []);
      setErreur(null);
    } catch (e) {
      setErreur(e);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  // Une recharge rapprochee dans la console doit apparaitre des le retour
  // sur l'onglet, pas seulement a la prochaine ouverture de l'ecran.
  useRelectureAuRetour(() => void charger());

  // Fiche client exigee a la premiere recharge (08/10/2026) : le serveur
  // refuse (409) tant qu'elle manque ; la fiche s'affiche alors, et la
  // recharge repart une fois enregistree.
  const [ficheAFaire, setFicheAFaire] = useState(false);

  const demander = async () => {
    setEnvoi(true);
    setErreur(null);
    try {
      setNouvelle(await api.demanderRecharge(montantChoisi));
      setFicheAFaire(false);
      await charger();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && /fiche client/i.test(e.detail ?? "")) setFicheAFaire(true);
      else setErreur(e);
    } finally {
      setEnvoi(false);
    }
  };

  if (chargement) return <Chargement />;
  const enAttente = recharges.filter((r) => r.status_id === 1);

  return (
    <>
      <h1 className="page-title">Facturation</h1>
      <p className="page-lead">Votre crédit, vos recharges et vos factures.</p>

      <Erreur erreur={erreur} />

      <div className="grid" style={{ marginBottom: "1.5rem" }}>
        <div className="stat">
          <p className="stat__label">Crédit disponible</p>
          <p className="stat__value">{montant(solde?.balance_minor, solde?.currency_code)}</p>
        </div>
        <div className="stat">
          <p className="stat__label">Commandes en attente</p>
          <p className="stat__value">
            {recharges.filter((r) => r.status_id === 1).length}
          </p>
        </div>
      </div>

      {/* Coordonnees de virement d'une demande qui vient d'etre creee :
          c'est l'information dont le client a besoin TOUT DE SUITE. */}
      {nouvelle && (
        <div className="secret-once" style={{ marginBottom: "1.5rem" }}>
          <strong>Bon de commande émis — effectuez maintenant le virement</strong>
          <div className="table-wrap">
            <table>
              <tbody>
                {/* Le montant a VIRER est le TTC, pas le credit. Les
                    confondre ferait virer le mauvais montant : le
                    rapprochement ne tomberait pas juste, et le client se
                    croirait a jour en devant encore la TVA. */}
                <tr>
                  <th style={{ width: "13rem" }}>Montant à virer</th>
                  <td className="num" style={{ textAlign: "left", fontSize: "1.15rem", fontWeight: 700 }}>
                    {montant(nouvelle.amount_ttc_minor ?? nouvelle.amount_minor, nouvelle.currency_code)}
                    <span className="muted" style={{ fontWeight: 400 }}> TTC</span>
                  </td>
                </tr>
                <tr>
                  <th>Crédit ajouté</th>
                  <td className="num" style={{ textAlign: "left" }}>
                    {montant(nouvelle.amount_minor, nouvelle.currency_code)}
                    <span className="muted"> hors taxes</span>
                    {(nouvelle.vat_amount_minor ?? 0) > 0 && (
                      <div className="muted">
                        dont {montant(nouvelle.vat_amount_minor, nouvelle.currency_code)} de TVA,
                        qui n'est pas convertie en crédit
                      </div>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Bénéficiaire</th>
                  <td>{nouvelle.beneficiaire}</td>
                </tr>
                <tr>
                  <th>IBAN</th>
                  <td>
                    <code>{nouvelle.iban}</code>
                  </td>
                </tr>
                {nouvelle.bic && (
                  <tr>
                    <th>BIC</th>
                    <td>
                      <code>{nouvelle.bic}</code>
                    </td>
                  </tr>
                )}
                <tr>
                  <th>Référence à indiquer</th>
                  <td>
                    <code>{nouvelle.reference}</code>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p style={{ margin: "0.6rem 0 0" }}>
            <strong>Indiquez impérativement la référence</strong> dans le libellé du virement :
            c'est elle qui permet de rattacher le paiement à votre compte. Le crédit est ajouté
            après réception du virement, et votre facture acquittée est alors disponible ici.
          </p>
        </div>
      )}

      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <h2 className="card__title" style={{ fontSize: "1.1rem" }}>
          Commander du crédit
        </h2>
        <p className="card__lead">
          Le paiement se fait par virement bancaire. Comptez le délai habituel de votre banque
          entre l'ordre et la réception — anticipez si vous avez des envois prévus.
        </p>

        {!peutRecharger && (
          <div className="alert alert--info">
            Votre rôle vous permet de consulter la facturation, mais pas d'engager une
            dépense. Demandez une recharge à un administrateur de votre organisation.
          </div>
        )}

        {peutRecharger && ficheAFaire && (
          <FicheClient titre="Avant votre première recharge : votre fiche client" onEnregistree={() => void demander()} />
        )}

        {peutRecharger && (
        <>
        <div className="row" style={{ marginBottom: "1rem" }}>
          {MONTANTS_SUGGERES.map((m) => (
            <button
              key={m}
              type="button"
              className={m === montantChoisi ? "btn btn--primary" : "btn btn--secondary"}
              onClick={() => setMontantChoisi(m)}
            >
              {montant(m)}
            </button>
          ))}
        </div>

        <div className="field" style={{ maxWidth: "16rem" }}>
          <label className="field-label" htmlFor="autre">
            Autre montant (en euros)
          </label>
          <input
            id="autre"
            className="input"
            inputMode="numeric"
            value={Math.round(montantChoisi / 100)}
            onChange={(e) => {
              const euros = Number(e.target.value.replace(/\D/g, "")) || 0;
              setMontantChoisi(euros * 100);
            }}
          />
        </div>

        <div className="actions">
          <button
            type="button"
            className="btn btn--primary"
            disabled={envoi || montantChoisi < 1000}
            onClick={() => void demander()}
          >
            Commander
          </button>
          {montantChoisi < 1000 && <span className="muted">Minimum 10,00 EUR.</span>}
        </div>
        </>
        )}
      </div>

      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <h2 className="card__title" style={{ fontSize: "1.1rem" }}>
          Commandes en attente de paiement
        </h2>
        {/* Seulement les commandes non payees (Franck, 08/10/2026) : une
            commande payee devient une facture, listee plus bas. */}
        {enAttente.length === 0 ? (
          <Vide>Aucune commande en attente. Les commandes payées figurent dans vos factures.</Vide>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Référence</th>
                  <th className="num">Montant</th>
                  <th>Émis le</th>
                  <th>État</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {enAttente.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <code>{r.reference}</code>
                    </td>
                    <td className="num">{montant(r.amount_minor, r.currency_code)}</td>
                    <td>{date(r.created_at)}</td>
                    <td>
                      {r.status_id === 2 ? (
                        <span className="pill pill--ok">Créditée</span>
                      ) : r.status_id === 3 ? (
                        <span className="pill pill--muted">Annulée</span>
                      ) : (
                        <span className="pill pill--pending">En attente du virement</span>
                      )}
                    </td>
                    <td className="num">
                      <button
                        type="button"
                        className="btn btn--link"
                        onClick={() =>
                          void (async () => {
                            try {
                              telecharger(await api.bonDeCommandePdf(r.id), `BC-${r.reference}.pdf`);
                            } catch (e) {
                              setErreur(e);
                            }
                          })()
                        }
                      >
                        PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2 className="card__title" style={{ fontSize: "1.1rem" }}>
          Factures
        </h2>
        {factures.length === 0 ? (
          <Vide>Aucune facture pour le moment.</Vide>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Numéro</th>
                  <th>Émise le</th>
                  <th className="num">HT</th>
                  <th className="num">TVA</th>
                  <th className="num">TTC</th>
                  <th>État</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {factures.map((f) => (
                  <tr key={f.id}>
                    <td>{f.number}</td>
                    <td>{date(f.issued_at)}</td>
                    <td className="num">{montant(f.amount_ht_minor, f.currency_code)}</td>
                    <td className="num">
                      {montant(f.vat_amount_minor, f.currency_code)}
                      {f.vat_regime_code === "ue_autoliquidation" && (
                        <div className="muted">autoliquidation</div>
                      )}
                    </td>
                    <td className="num">{montant(f.amount_ttc_minor, f.currency_code)}</td>
                    <td>
                      {f.status_id === 2 ? (
                        <span className="pill pill--ok">Acquittée</span>
                      ) : f.status_id === 3 ? (
                        <span className="pill pill--muted">Annulée</span>
                      ) : (
                        <span className="pill pill--pending">En attente</span>
                      )}
                    </td>
                    <td className="num">
                      <button
                        type="button"
                        className="btn btn--link"
                        onClick={() =>
                          void (async () => {
                            try {
                              telecharger(await api.facturePdf(f.id), `${f.number}.pdf`);
                            } catch (e) {
                              setErreur(e);
                            }
                          })()
                        }
                      >
                        PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
